import { Close, Delete, Edit, RestartAlt, Save } from '@mui/icons-material'
import { Box, Chip, Divider, Grid, IconButton, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MainButton } from 'src/components/MainButton'
import { ILocalitySolidityAndCircumference } from 'src/generated-types/locality-solidity-circumference'
import { Flex } from 'src/styles/flexComponent'
import { generateUUID } from 'src/utils/function'
import { z } from 'zod'
import { useGetCustomerUsers } from '../api/customer'
import { DmsCoordinateFormField } from '../components/IMaskInputField'
import BasicDatePicker from '../components/DateTimePicker'
import {
  AppForm,
  AppFormProps,
  FormCheckbox,
  FormFileCoreUpload,
  FormSelectField,
  FormTextField,
} from '../components/FormCore'
import { GenericDialogLayout } from '../components/GenericDialogLayout'
import { IFile } from '../generated-types'
import { useZodForm } from '../hooks/zod-form'
import { EdgeCase, UserDetail, getUserDetail } from '../utils/getUserDetails'
import LocalityFormMaintenances from './LocalityFormMaintenances'

export interface LocalityFormProps extends AppFormProps {
  file?: IFile
  title?: string
  isLoading?: boolean
  customerId?: number
  logo?: IFile
  isLoadingLogo?: boolean
}

export const LocalityForm: React.FC<LocalityFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  file,
  title,
  isLoading,
  customerId,
  logo,
  isLoadingLogo,
}) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const isUpdateForm = initialValues?.id
  const properties = ['solidity', 'circumference']
  const [solidityAndCircumferences, setsolidityAndCircumferences] = useState<Array<ILocalitySolidityAndCircumference>>(
    [],
  )
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null)
  const [image, setImage] = useState<IFile | string | null>(initialValues?.logoUrl || null)

  const schema = z.object({
    name: z.string().nonempty(translate('nameIsRequired', { ns: 'locality' })),
    locationNr: z.string().nonempty(translate('locationNrIsRequired', { ns: 'locality' })),
    latitude: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => {
        if (value === null || value === '' || value === undefined) return null
        return typeof value === 'string' ? parseFloat(value) : value
      })
      .refine(
        value => {
          if (value === null || value === undefined) return true
          return value >= -90 && value <= 90
        },
        {
          message: translate('latitudeMustBeBetween', { ns: 'locality' }),
        },
      )
      .optional(),
    longitude: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => {
        if (value === null || value === '' || value === undefined) return null
        return typeof value === 'string' ? parseFloat(value) : value
      })
      .refine(
        value => {
          if (value === null || value === undefined) return true // Skip validation if empty
          return value >= -180 && value <= 180
        },
        {
          message: translate('longitudeMustBeBetween', { ns: 'locality' }),
        },
      )
      .optional(),
    notificationActive: z.boolean().default(false),
    active: z.boolean().default(true),
    fileId: z.any().optional(),
    logoId: z.any(),
    certifiedDate: z.date().or(z.string()).or(z.null()).optional(),
    certificateDueDate: z.date().or(z.string()).or(z.null()).optional(),
    responsibleUserId: z.string().or(z.null()).optional(),
    responsibleUser: z
      .object({
        id: z.string(),
        email: z.string(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
        phoneNumber: z.string().nullable().optional(),
      })
      .optional()
      .nullable()
      .transform(value => (value ? value.id : undefined)),
    hasFish: z.boolean().optional(),
    hs: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    vC5M: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    vC15M: z
      .string()
      .or(z.number())
      .or(z.null())
      .transform(value => (typeof value === 'string' ? (value == '' ? null : Number(value)) : value))
      .refine(value => value == null || value >= 0, { message: translate('mustBeZeroOrPositive', { ns: 'common' }) })
      .optional(),
    solidityAndCircumferences: z
      .array(
        z.object({
          localityId: z.number().or(z.null()).optional(),
          solidity: z.number().or(z.string()),
          circumference: z.number().or(z.string()),
        }),
      )
      .or(z.null())
      .optional(),
    localityMaintenanceTemplates: z
      .array(
        z.object({
          id: z.number(),
          maintenanceTemplateId: z.number(),
          name: z.string(),
          startDate: z.union([z.date(), z.string().transform(d => new Date(d))]).nullable(),
          repeatEnds: z
            .union([z.date(), z.string().transform(d => new Date(d))])
            .optional()
            .nullable(),
        }),
      )
      .optional(),
    solidity: z.number().nullable().optional(),
    circumference: z.number().nullable().optional(),
    country: z.string().or(z.null()).optional(),
    municipality: z.string().or(z.number()).or(z.null()).optional(),
  })

  const handleChange = (e, value) => {
    form.setValue('responsibleUser', value)
    form.setValue('responsibleUserId', value?.id)
  }

  type FormSchema = z.infer<typeof schema>
  const form: UseFormReturn<FormSchema> = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      solidity: initialValues?.solidity ?? null,
      circumference: initialValues?.circumference ?? null,
    },
  })

  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId)

  useEffect(() => {
    setImage(form.getValues('logoId'))
  }, [image])

  useEffect(() => {
    if (initialValues?.solidityAndCircumferences) {
      initialValues?.solidityAndCircumferences.forEach(property => {
        property.uniqueId = generateUUID()
      })
      setsolidityAndCircumferences(initialValues?.solidityAndCircumferences)
    }
  }, [initialValues])

  useEffect(() => {
    form.setValue('solidityAndCircumferences', solidityAndCircumferences)
  }, [solidityAndCircumferences, form])

  const resetProperties = () => {
    properties.forEach(key => {
      form.clearErrors(key as any)
      form.setValue(key as any, null)
    })
  }

  const validateProperty = key => {
    const value = form.getValues(key)
    if (!value) {
      form.setError(key, { type: 'required', message: translate('required', { ns: 'common' }) })
      return false
    }

    const numberValue = Number(value)
    if (isNaN(numberValue) || numberValue <= 0) {
      form.setError(key, { type: 'invalid', message: translate('mustBePositive', { ns: 'common' }) })
      return false
    }

    form.clearErrors(key)
    return true
  }

  const validateProperties = () => {
    let isValid = true
    properties.forEach(key => {
      if (!validateProperty(key)) {
        isValid = false
      }
    })
    return isValid
  }

  const handleSave = () => {
    const solidity = form.getValues('solidity')
    const circumference = form.getValues('circumference')

    if (!validateProperties()) {
      return
    }
    setsolidityAndCircumferences(prev => {
      if (editingPropertyId) {
        return prev.map(p => {
          if (p.uniqueId === editingPropertyId) {
            return {
              solidity: solidity,
              circumference: circumference,
              uniqueId: editingPropertyId,
            } as ILocalitySolidityAndCircumference
          }
          return p
        })
      }
      return [
        ...prev,
        {
          solidity: solidity,
          circumference: circumference,
          uniqueId: generateUUID(),
        } as ILocalitySolidityAndCircumference,
      ]
    })
    setEditingPropertyId(null)
    resetProperties()
  }

  const handleMainButtonClick = async () => {
    const solidity = form.getValues('solidity')
    const circumference = form.getValues('circumference')

    if (solidity && circumference) {
      await new Promise(resolve => {
        handleSave()
        setTimeout(resolve, 0)
      })
      form.handleSubmit(onSubmit)()
    } else if (!solidity && !circumference) {
      form.handleSubmit(onSubmit)()
    } else {
      validateProperties()
    }
  }

  const showingProperties = useMemo(() => {
    return solidityAndCircumferences.filter(x => x.uniqueId != editingPropertyId)
  }, [editingPropertyId, solidityAndCircumferences])

  return (
    <GenericDialogLayout
      title={title}
      pop={onCancel}
      actions={[
        <MainButton
          key="locality-form-button"
          variant="contained"
          loading={isLoading}
          onClick={handleMainButtonClick}
          data-cy="UpdateLocality_submit"
        >
          {isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        </MainButton>,
      ]}
      removedCloseButton={false}
    >
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues}>
        <FormTextField
          name="name"
          label="Name"
          defaultValue={initialValues?.Name}
          inputProps={{
            style: { textTransform: 'capitalize' },
          }}
        />
        <Grid container>
          <Grid item sm={12}>
            <Grid container>
              <Grid item xs={12} sm={4}>
                <FormTextField name="locationNr" label="Location number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField name="country" label={translate('county', { ns: 'locality' })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormTextField name="municipality" label={translate('Municipality', { ns: 'locality' })} />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={7} sm={7}>
                <Grid item xs={12}>
                  <DmsCoordinateFormField direction="latitude" name="latitude" label="Gps latitude" />
                </Grid>
                <Grid item xs={12}>
                  <DmsCoordinateFormField direction="longitude" name="longitude" label="Gps longitude" />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <BasicDatePicker
                    label={translate('certifiedDate', { ns: 'locality' })}
                    name="certifiedDate"
                    onChange={value => {
                      form.setValue('certifiedDate', value)
                      form.trigger('certifiedDate')
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <BasicDatePicker
                    label={translate('certificateDueDate', { ns: 'locality' })}
                    name="certificateDueDate"
                  />
                </Grid>
              </Grid>
              <Grid item xs={5} sm={5}>
                <FormFileCoreUpload
                  userMessage={translate('image-upload-user-message', { ns: 'common' })}
                  name="logoId"
                  mapFileKey="id"
                  initialFiles={logo}
                  inactiveMsg={translate('chooseCertificate', { ns: 'locality' })}
                  getUploadedFiles={file => setImage(file)}
                  height={image ? '150px' : '225px'}
                  acceptedFileTypes={['image/png', 'image/jpeg', 'image/gif', 'image/jpg', 'image/webp']}
                  isFilesLoading={isLoadingLogo}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={4}>
            <FormTextField type="number" name="hs" label={translate('hs', { ns: 'common' })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormTextField type="number" name="vC5M" label={translate('vc5m', { ns: 'common' })} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormTextField type="number" name="vC15M" label={translate('vc15m', { ns: 'common' })} />
          </Grid>
        </Grid>
        <Flex.Column px={1} py={1} justifyContent={'center'} alignItems={'flex-start'}>
          <Typography variant="subtitle2">{translate('solidityAndCircumference', { ns: 'locality' })}</Typography>
          <Divider />
          {showingProperties.map((property, index) => (
            <Flex.Row py={1} justifyContent={'center'} alignItems={'center'} key={index} gap={1}>
              {`#${index + 1}`}
              <Chip label={`${translate('solidity', { ns: 'locality' })} : ${property.solidity}`} />
              <Chip label={`${translate('circumference', { ns: 'locality' })} : ${property.circumference}`} />
              <Flex.Row gap={1} justifyContent={'center'} alignItems={'center'}>
                <IconButton
                  onClick={() => {
                    const appForm = form as any
                    appForm.setValue('solidity', property.solidity)
                    appForm.setValue('circumference', property.circumference)
                    setEditingPropertyId(property.uniqueId)
                  }}
                  sx={{ width: '30px', height: '30px' }}
                  disabled={editingPropertyId !== null}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setsolidityAndCircumferences(prev => prev.filter(p => p.uniqueId !== property.uniqueId))
                  }}
                  sx={{ width: '30px', height: '30px' }}
                  disabled={editingPropertyId !== null}
                >
                  <Delete />
                </IconButton>
              </Flex.Row>
            </Flex.Row>
          ))}
          <Flex.Row py={1} justifyContent={'center'} alignItems={'center'}>
            <FormTextField
              type={'number'}
              name="solidity"
              label={translate('solidity', { ns: 'locality' })}
              focused={editingPropertyId && showingProperties.length == 0 ? true : undefined}
            />
            <FormTextField
              type={'number'}
              name="circumference"
              label={translate('circumference', { ns: 'locality' })}
              focused={editingPropertyId && showingProperties.length == 0 ? true : undefined}
            />
            <Flex.Row gap={1}>
              <IconButton onClick={handleSave}>
                <Save />
              </IconButton>
              {editingPropertyId ? (
                <IconButton
                  onClick={() => {
                    setEditingPropertyId(null)
                    resetProperties()
                  }}
                >
                  <Close />
                </IconButton>
              ) : (
                <IconButton onClick={resetProperties}>
                  <RestartAlt />
                </IconButton>
              )}
            </Flex.Row>
          </Flex.Row>
        </Flex.Column>
        <FormSelectField
          name="responsibleUser"
          data={customerUsers!}
          label={translate('responsibleUser', { ns: 'locality' })}
          getOptionLabel={option => getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
          renderOption={(props: any, option) => (
            <Box {...props} key={option}>
              {getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
            </Box>
          )}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={handleChange}
        />
        <LocalityFormMaintenances form={form} customerId={customerId} initialValues={initialValues} />
        <FormFileCoreUpload
          label={translate('fileName', { ns: 'common' })}
          name="fileId"
          mapFileKey="id"
          initialFiles={file}
          inactiveMsg={translate('chooseCertificate', { ns: 'locality' })}
          height="150px"
        />
        <Box py={1}>
          <FormCheckbox name="notificationActive" label={translate('notificationActive', { ns: 'locality' })} />
          <FormCheckbox name="active" label={translate('active', { ns: 'locality' })} />
          {initialValues?.netCount > 0 && (
            <FormCheckbox name="hasFish" label={translate('hasFish', { ns: 'locality' })} />
          )}
        </Box>
      </AppForm>
    </GenericDialogLayout>
  )
}
